import { useEffect, useState } from "react";
import { Menu, PhoneCall, X, Zap } from "lucide-react";
import Logo from "./Logo";

const LINKS = [
  { id: "services", label: "Services", tid: "nav-link-services" },
  { id: "dual", label: "Dual Climate", tid: "nav-link-dual" },
  { id: "coverage", label: "Coverage", tid: "nav-link-coverage" },
  { id: "reviews", label: "Reviews", tid: "nav-link-reviews" },
  { id: "contact", label: "Contact", tid: "nav-link-contact" },
  { id: "booking", label: "Booking", tid: "nav-link-booking" },
  { id: "gallery", label: "Gallery", tid: "nav-link-gallery" },
];

export const scrollToSection = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -84 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export default function Navbar({ onQuote }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToSection(id);
  };

  return (
    <header
      data-testid="navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${
        scrolled || open
          ? "border-b border-white/10 bg-navy-950/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => window.__lenis?.scrollTo(0)}
          className="transition-transform duration-200 hover:scale-[1.03]"
          aria-label="Crowson home"
        >
          <Logo />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.tid}
              onClick={() => go(l.id)}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300 transition-colors duration-200 hover:text-forge"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <a
            data-testid="nav-cta-emergency"
            href="tel:+16155932628"
            className="group flex items-center gap-2 rounded-full border border-ice/30 bg-ice/10 px-4 py-2 text-xs font-semibold text-ice transition-colors duration-200 hover:border-ice/60 hover:bg-ice/20"
          >
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-ice" />
            <PhoneCall className="h-3.5 w-3.5" />
            615-593-2628
          </a>
          <button
            data-testid="nav-cta-quote"
            onClick={onQuote}
            className="flex items-center gap-2 rounded-full bg-gradient-to-br from-forge to-forge-hi px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white shadow-[0_8px_30px_rgba(255,107,0,0.35)] transition-transform duration-200 hover:scale-105 active:scale-95"
          >
            <Zap className="h-3.5 w-3.5" />
            Instant Quote
          </button>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="grid h-10 w-10 place-items-center rounded-lg border border-white/10 text-slate-200 lg:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="border-t border-white/10 bg-navy-950/95 backdrop-blur-xl lg:hidden">
          <div className="space-y-1 px-6 py-6">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.tid}-mobile`}
                onClick={() => go(l.id)}
                className="block w-full py-3 text-left font-display text-lg font-semibold text-slate-100"
              >
                {l.label}
              </button>
            ))}
            <div className="flex flex-col gap-3 pt-4">
              <a
                data-testid="nav-cta-emergency-mobile"
                href="tel:+16155932628"
                className="flex items-center justify-center gap-2 rounded-full border border-ice/30 bg-ice/10 px-4 py-3 text-sm font-semibold text-ice"
              >
                <PhoneCall className="h-4 w-4" /> 615-593-2628
              </a>
              <button
                data-testid="nav-cta-quote-mobile"
                onClick={() => { setOpen(false); onQuote(); }}
                className="rounded-full bg-gradient-to-br from-forge to-forge-hi px-4 py-3 text-sm font-bold uppercase tracking-wider text-white"
              >
                Instant Quote
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
